import { FileText, MoreVertical, RefreshCw } from "lucide-react";
import { useResumesContext } from "../context/resumes-context";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { LastFetchedAt } from "../components/timers/LastFetchedAt";
import { PopupMenu } from "../components/popups/PopupMenu";
import { AnimatePresence } from "framer-motion";
import { DeletePopup, PopupWindow, SharePopup } from "../components";
import { deleteResume } from "../api/resume";
import { removeUserCache } from "../utils/client_storage_utils";

export const MyResumesView = () => {

    const { resumes, loading, error, lastFetched, refreshResumes } = useResumesContext()
    const navigate = useNavigate()

    const [moreMenuResumeId, setMoreMenuResumeId] = useState(null);
    const [shareResumeId, setShareResumeId] = useState(null);
    
    const [deleteResumeId, setDeleteResumeId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deletingSuccess, setDeletingSuccess] = useState(false);
    const [deletingError, setDeletingError] = useState(false);
    const moreBtnRef = useRef(null);
    

    let pageTitle = "Here are your saved resume analyses. Track your progress for different roles."
    if(resumes.length === 0){
        pageTitle = "Upload a resume to get personalized insights—your saved analyses will show up here!"
    }
    const optionsBtnList = [
        { label: "View", onClick: () => { 
            navigate(`/myresumes/${moreMenuResumeId}`);
            setMoreMenuResumeId(null);
            setShareResumeId(null);
            setDeleteResumeId(null);
         } },
        { label: "Share", onClick: () => { 
            setShareResumeId(moreMenuResumeId); 
            setDeleteResumeId(null); } },
        { label: "Delete", onClick: () => { 
            setDeleteResumeId(moreMenuResumeId); 
            setShareResumeId(null);
            setDeletingSuccess(false);
            setDeletingError(false);
        } }
    ]

    const handleDeleteResumeConfirm = async (resumeId) => {
        setDeleting(true);
        try {
            const {success, resp} = await deleteResume(resumeId)
            if(success){
                setDeletingSuccess(true);
                refreshResumes()
            }else{
                setDeletingError(resp || "Failed to delete resume");
                if(resp?.toLowerCase() === "invalid user identity"){
                    removeUserCache()
                    navigate("/")
                }
            }
        } catch (error) {
            setDeletingSuccess(false);
            setDeletingError("Failed to delete resume");
        }finally{
            setDeleting(false);
        }
    }

    return (
        <section className="bg-gradient-to-b from-white to-indigo-50 px-4 sm:px-8 py-20 w-full h-full flex-1 flex flex-col">
            <div className="container fade-in max-w-5xl mx-auto flex-1 flex flex-col">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">My Resumes</h2>
                <div className="flex items-center gap-2 text-gray-600">
                    {lastFetched && <p className=" text-sm ">{<LastFetchedAt timestamp={lastFetched} />}</p>}
                    <RefreshCw size={16} className={`cursor-pointer ${ loading ? 'animate-spin' : '' }`} onClick={refreshResumes} />

                </div>

                <p className="text-gray-600 mb-8">{pageTitle}</p>
                {error && <p className="text-red-500 font-semibold mb-8">{"*" + error}</p>}
                {resumes.length === 0 && 
                <button className="bg-indigo-600 text-white px-8 py-3 rounded-md hover:bg-indigo-700 transition text-lg font-medium w-max" onClick={() => { navigate('/') }}>
                    Analyze My Resume Now
                </button> }
                <div className="space-y-4">
                    {resumes.map(resume => (
                        <div className="flex flex-col" key={resume.id}>
                            <div className=" bg-white py-2 px-2 sm:px-4 rounded-xl border flex items-center justify-between hover:shadow-md transition-shadow">
                                <div className="flex items-center w-full gap-2">
                                    <div className="flex items-center flex-1 min-w-0 justify-start cursor-pointer relative" onClick={ () =>{ navigate(`/myresumes/${resume.id}`) }}>
                                        <div className="relative flex-shrink-0">
                                            <FileText className="text-indigo-500 h-8 w-8 mr-4"/>
                                            {resume?.ai_generated && <p className="absolute -top-4 right-2 font-bold text-2xl">✨</p>}
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <p className="font-bold text-gray-800 truncate">{resume.filename}</p>
                                            <p className="text-sm text-gray-500 truncate">Role: {resume.role}</p>
                                            <p className="text-sm text-gray-500 truncate">created: <span className="xfont-semibold">{new Date(resume.created_at).toLocaleDateString()}</span></p>
                                        </div>
                                    </div>
                                    {resume?.ai_generated && 
                                    <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap flex-shrink-0">
                                        <span className="hidden sm:inline">AI GENERATED</span>
                                        <span className="sm:hidden">AI</span>
                                    </div>
                                    }
                                    <div ref={moreBtnRef} className="relative p-2 cursor-pointer flex-shrink-0 text-gray-600 hover:text-red-600" title="Menu" onClick={e => { 
                                        e.stopPropagation();
                                        setMoreMenuResumeId( moreMenuResumeId === resume.id ? null : resume.id )
                                        }}>
                                        <MoreVertical size={20}/>
                                        <AnimatePresence>
                                            {moreMenuResumeId === resume.id && 
                                                <PopupMenu moreBtnRef={moreBtnRef} setMoreMenuResumeId={setMoreMenuResumeId} moreMenuResumeId={moreMenuResumeId} resume={resume} optionsBtnList={optionsBtnList} />}
                                        </AnimatePresence>
                                    </div>
                                    {shareResumeId === resume.id && 
                                    <PopupWindow isOpen={true} onClose={() => { setShareResumeId(null); setMoreMenuResumeId(null); }}  >
                                        <SharePopup 
                                            title={`Sharing ${resume.filename}`}
                                            subTitle={`created on ${resume.created_at}`}
                                            resumeId={resume.id}
                                            onCancel={() => { setShareResumeId(null); setMoreMenuResumeId(null); }}
                                            />
                                    </PopupWindow>}
                                    {deleteResumeId === resume.id && 
                                    <PopupWindow isOpen={true} onClose={() => { setDeleteResumeId(null); setMoreMenuResumeId(null); setDeletingSuccess(false); setDeletingError(false); }}  >
                                        <DeletePopup
                                            loading={deleting}
                                            deletingError={deletingError}
                                            deleteSuccess={deletingSuccess}
                                            onCancel={() => { setDeleteResumeId(null); setMoreMenuResumeId(null); setDeletingSuccess(false); setDeletingError(false); }}
                                            onConfirm={() => handleDeleteResumeConfirm(resume.id)}
                                            message={<p className="text-sm text-gray-500 mb-6">Are you sure you want to delete the <span className="font-semibold text-gray-800">{resume.filename}</span> resume, this action cannot be undone?</p>}
                                            title="Delete Resume"
                                        />
                                    </PopupWindow>}
                                </div>
                            </div>
                        </div>
                        
                    ))}
                </div>
            </div>
        </section>
    );
};